/* global navigator */
import SocketConstructor, { Socket } from 'socket.io-client';
import { notify } from 'app/services/errorReporter';
import { AbortedError, ResponseError, TimeoutError } from 'app/constants/ResponseErrors';
import {
  callbackAsPromise,
  sleep,
} from 'app/utils/async';
import { throwIfCancelled } from 'app/utils/cancelToken';
import TaskQueue from 'app/utils/queues/TaskQueue';
import Logger from 'app/services/logger';
import { bindSocketEvents, unbindSocketEvents } from 'app/utils/events';
import { getGlobalHistory } from 'app/services/history';
import { parseUrlLoose } from 'app/utils/LooseURL';
import RTCPeer from 'app/services/rtc/Peer';
import { EventEmitter } from 'events';
import {
  RTC_ON,
  RTC_OFF,
  RTC_CONNECTING,
  RTC_FAILED,
  RTC_ROOM_EVENTS,
  Caller,
  WebRTCFeatures,
  IClientFeatures,
  ClientDataDecoder,
  PARTICIPANT_NAME_MAX_LENGTH,
} from 'app/services/rtc/constants';
import bindMethods from 'app/utils/bindMethods';
import { StreamActions, ActionStreamEvents, StreamEvents, ActionStream, EncodedAction } from 'app/services/rtc/sync/ActionStream';
import { GlobalPlatformSupport, featuresIntersection } from './PlatformSupport';
import TrackState from './TrackState';
import { BotVersion, BotCommand, BotCommandArgs, IBotCommandAck } from './BotCommand';
import FluentdLogger from 'app/services/fluentdLogger';
import { dumpErrorInfo } from 'app/utils/errors/next';
import { ACTION_SCHEMA_VERSION } from '@engoo/realtime-protocol/versioning';
import { Set } from 'immutable';
import { Stream } from '@engoo/realtime-protocol/stream';
import { ValuesOf, DistributiveOmit } from 'app/utils/types';
import { EventHandlerMap } from 'app/utils/EventMapEmitter';
import { IEncodedPersistedAction, StreamPosition } from '@engoo/realtime-protocol/persisted';
import { IMediaStatus } from 'app/containers/rtc/PeersContext';
import { BotMessageType } from '@engoo/realtime-protocol/signaling/bot';
import { SocketAbortedError, SocketTimeoutError } from 'app/constants/SocketErrors';
import { decodeOrElse } from 'app/utils/ioTs';
import { EntityStringId } from 'app/types/entity';
import {
  RoomActions, IActionStreamStateChanged,
  IJoinResponse, IRelayedAction,
  IStunServer, ITurnServer, ILockAction,
  IStreamActionWithPayloadMap, IRoomActionsPayload,
  IRoomActionsReturnTypes, ISubscriptionRequest,
  ISubscriptionResponse, RequestedStreams,
  IStreamActionWithPayloadReturnTypes,
  IRelayedMessage, IMessagePayload, IPeerJoinedEvent,
  IPeerLeaveEvent, RtcStatsEvent, RoomEvents,
} from 'app/services/rtc/RealtimeProtocolTypes';
import { IdentityEntityStringIdCodec } from 'app/types/entity/codecs';
import { ImpossibleJoinError } from './ImpossibleJoinError';

// Wait long enough for the participant to be kicked out from the
// server first. Make it longer than 25 + 5 seconds which is default
// timeout for socket.io server: pingInterval + pingTimeout. This
// timeout will then only trigger when the client is active not
// replying for some reason.
// https://github.com/iknow/issues/issues/114#issuecomment-574075834
export const ACK_TIMEOUT = 35 * 1000;

const gatherClientMetadata = () => ({
  userAgent: navigator.userAgent,
});

interface ISocketEventMap {
  connect: unknown;
  connect_error: Error;
  disconnect: string;
  [RoomEvents.Redirect]: unknown;
  [RoomEvents.Message]: IRelayedMessage;
  [RoomEvents.Join]: IPeerJoinedEvent;
  [RoomEvents.Remove]: IPeerLeaveEvent;
  [RoomEvents.Stats]: unknown;
  [StreamEvents.ACTION]: IRelayedAction;
  [StreamEvents.METADATA_CHANGED]: IActionStreamStateChanged;
  error: Error;
}

interface IDebugMethods {
  failPeerConnection(id: EntityStringId): void;
}

interface IConnectOptions {
  id: EntityStringId;
  url: string;
  caller: Caller;
  token: string;
  upgradedToUser: boolean;
  requiredFeatures: Set<WebRTCFeatures>;
  displayName?: string;
}

const Transports = {
  WebSocket:            ['websocket'],
  PollingThenWebSocket: ['polling', 'websocket'],
};

interface IActionStreamsMap extends Map<
  Stream,
  ActionStream<Stream>
> {
  get<S extends Stream>(key: S): ActionStream<S> | undefined;
  set<S extends Stream, V extends ActionStream<S>>(key: S, value: V): this;
}

export default class RTCRoom extends EventEmitter {
  private authorized = false;
  private joined = false;
  private id: EntityStringId | undefined;
  private url: string | undefined;
  private token: string | undefined;
  private callerUpgradedToUser: boolean = false;
  private socket: Socket | null = null;
  private serverTimeDelta: number = 0;
  private displayName: string | undefined;

  private requiredFeatures = Set<WebRTCFeatures>();
  private actionStreams: IActionStreamsMap = new Map();
  private joinQueue = new TaskQueue();
  private transports: string[] = Transports.WebSocket;
  private mediaStatus: IMediaStatus = {
    audio: TrackState.Off,
    video: TrackState.Off,
    speaking: false,
  };
  private localVideoStream: MediaStream | undefined;
  private localAudioStream: MediaStream | undefined;
  private localScreenStream: MediaStream | undefined;

  private clientFeatures: IClientFeatures | undefined;

  private socketEventMap: EventHandlerMap<ISocketEventMap> = bindMethods(this, {
    connect:                  this.onSocketConnected,
    connect_error:            this.onConnectError,
    disconnect:               this.onSocketDisconnected,
    [RoomEvents.Redirect]:    wrapReceive(RoomEvents.Redirect, this.reconnect),
    [RoomEvents.Message]:     wrapReceive(RoomEvents.Message, this.dispatchReceivedMessage),
    [RoomEvents.Join]:        wrapReceive(RoomEvents.Join, this.peerJoined),
    [RoomEvents.Remove]:      wrapReceive(RoomEvents.Remove, this.peerLeft),
    [StreamEvents.ACTION]:    wrapReceive(StreamEvents.ACTION, this.onAction),
    [StreamEvents.METADATA_CHANGED]:
      wrapReceive(StreamEvents.METADATA_CHANGED, this.onActionsMetadataChanged),

    error: (error: Error) => {
      this.emit(RTC_ROOM_EVENTS.signalingError, error);

      Logger.error(error.message);
      FluentdLogger.warn({
        name:       'Room.error',
        room_id:    this.id,
        socket_url: this.url,
        error:      dumpErrorInfo(error),
      });
    },
  });

  public peers = new Map<EntityStringId, RTCPeer>();
  public caller: Caller | null = null;
  public debug: IDebugMethods | undefined;
  public stunservers: IStunServer[] | undefined;
  public turnservers: ITurnServer[] | undefined;

  public constructor() {
    super();

    if (process.env.NODE_ENV !== 'production') {
      this.debug = {
        failPeerConnection: id => {
          const peer = this.peers.get(id);

          if (!peer || !('debug' in peer.connection)) {
            return;
          }

          peer.connection.debug.fail();
        },
      };
    }
  }

  private isConnected() {
    return this.socket?.connected ?? false;
  }

  public connect({ id, url, caller, token, upgradedToUser, requiredFeatures, displayName }: IConnectOptions) {
    // Close potential previous connection. If id is the same it means
    // we're reconnecting with a different url.
    this.close(RTC_CONNECTING);
    Logger.warn(`Connecting to RT on ${url}, transports: ${JSON.stringify(this.transports)}`);

    this.id                   = id;
    this.url                  = url;
    this.caller               = caller;
    this.callerUpgradedToUser = upgradedToUser;
    this.token                = token || undefined;
    this.requiredFeatures     = requiredFeatures;
    this.displayName          = displayName ? this.trimParticipantName(displayName) : undefined;

    const urlParts = parseUrlLoose(url, getGlobalHistory().location);

    if (process.env.NODE_ENV !== 'production' && /^localhost/.test(urlParts.hostname)) {
      // Replace localhost hostname with current url to use the same
      // origin in devmode
      urlParts.hostname = getGlobalHistory().location.hostname;
    }

    const { protocol, hostname, port, pathname, search } = urlParts;

    this.socket = SocketConstructor(`${protocol}//${hostname}${port ? `:${port}` : ''}`, {
      reconnection: false,
      transports:   this.transports,
      path:         pathname,
      query:        search.replace(/^\?/, ''),
    });

    bindSocketEvents(this.socket, this.socketEventMap);

    return this.socket.connect();
  }

  public close(reason = RTC_OFF) {
    // Clean-up ahead of closing the socket to guarantee no events get
    // fired on the socket
    this.cleanup();

    if (reason === RTC_OFF) {
      // After explicit disconnects, reset transports to websocket
      // only so that next attempt is going to start with this
      // strategy
      this.transports = Transports.WebSocket;
    }

    this.emit(RTC_ROOM_EVENTS.signalingStateChange, reason);
  }

  private onSocketConnected() {
    this.joinRoom();
  }

  private onConnectError(error: Error) {
    Logger.error(error.message);
    FluentdLogger.warn({
      name:       'Room.onConnectError',
      room_id:    this.id,
      socket_url: this.url,
      error:      dumpErrorInfo(error),
    });

    // In case the connection attempt failed with a websocket error,
    // retry with polling-first strategy
    if (error.message === 'websocket error') {
      Logger.warn('Downgrading connection to polling');
      this.transports = Transports.PollingThenWebSocket;
      this.reconnect();
    } else {
      this.emit(RTC_ROOM_EVENTS.signalingError, error);
      this.close(RTC_FAILED);
    }
  }

  private onSocketDisconnected(event: string) {
    Logger.warn(`RT server disconnected: ${event}`);
    this.cleanup();

    if (
      // Closed by user action
      event === 'io client disconnect' ||
      // Kicked out by server
      event === 'io server disconnect'
    ) {
      this.emit(RTC_ROOM_EVENTS.signalingStateChange, RTC_OFF);
    } else {
      this.reconnect();
    }
  }

  private onJoined(event: IJoinResponse) {
    this.joined = true;
    this.emit(RTC_ROOM_EVENTS.signalingStateChange, RTC_ON);

    const { peers, stunServers, turnServers, timestamp } = event;
    this.stunservers = stunServers;
    this.turnservers = turnServers;

    this.serverTimeDelta = new Date(timestamp).valueOf() - Date.now();

    for (const peer of peers) {
      this.peerJoined({ peer }, true);
    }
  }

  private reconnect() {
    this.close(RTC_CONNECTING);

    // Reconnect requires getting new token so it needs to be handled
    // by whatever makes API calls (RTCProvider in our case)
    this.emit(RTC_ROOM_EVENTS.reconnect);
  }

  public resetPeerConnection(id: EntityStringId) {
    const peer = this.peers.get(id);
    if (peer) {
      peer.resetConnection();
    }
  }

  private cleanup() {
    this.authorized = false;
    this.joined = false;

    if (this.socket) {
      unbindSocketEvents(this.socket, this.socketEventMap);

      if (this.isConnected()) {
        this.socket.close();
      }
    }

    // If they're any pending joins, abort them
    this.joinQueue.abort();

    this.peers.forEach(peer => {
      this.emit(RTC_ROOM_EVENTS.removePeer, peer.id, true);
      peer.close();
    });

    this.peers.clear();

    this.actionStreams.clear();

    this.socket = null;
  }

  private onAction({ stream, action }: IRelayedAction) {
    this.getActionStream(stream).emit(ActionStreamEvents.ACTION, action);
  }

  private onActionsMetadataChanged({ stream, ...metadata }: IActionStreamStateChanged) {
    this.getActionStream(stream).emit(ActionStreamEvents.METADATA_CHANGED, metadata);
  }

  private joinRoom() {
    this.joinQueue.push(async (cancelToken) => {
      try {
        if (!this.id || !this.token) {
          throw ImpossibleJoinError.build({
            id: this.id,
            token: this.token,
          });
        }

        this.clientFeatures = await GlobalPlatformSupport.getClientFeatures(this.requiredFeatures);

        Logger.warn(`Joining room ${this.id}`);

        throwIfCancelled(cancelToken);

        const authResponse = await this.send(RoomActions.Authorize, {
          clientVersion:  7,
          actionVersion:  ACTION_SCHEMA_VERSION,
          roomId:         this.id,
          token:          this.token,
        });
        throwIfCancelled(cancelToken);

        if (authResponse.result === 'redirect') {
          this.reconnect();
          return;
        } else if (authResponse.result !== 'success') {
          throw new ResponseError({
            statusText: 'Unable to parse Realtime authorization response',
            response: authResponse,
          });
        }
        this.authorized = true;

        const joinResponse = await this.send(RoomActions.Join, {
          clientData:    {
            upgradedToUser: this.callerUpgradedToUser,
            features: this.clientFeatures,
          },
          metadata:      gatherClientMetadata(),
          presence:      'member',
          name:          this.displayName,
        });
        throwIfCancelled(cancelToken);

        this.onJoined(joinResponse);
      } catch (error) {
        if (!cancelToken.isCancelled()) {
          notify(error, { name: 'joinRoom' });

          this.emit(RTC_ROOM_EVENTS.signalingError, error);

          this.close(RTC_FAILED);
        }
      }
    });
  }

  public async dumpAllSdps() {
    const result = [];

    for (const peer of this.peers.values()) {
      result.push({
        id: peer.id,
        ...await peer.dumpCurrentSdps(),
      });
    }

    return result;
  }

  public setLocalAudioStream(stream: MediaStream) {
    this.localAudioStream = stream;
    this.peers.forEach(peer => peer.setLocalAudioStream(stream));
  }

  public setLocalVideoStream(stream: MediaStream) {
    this.localVideoStream = stream;
    this.peers.forEach(peer => peer.setLocalVideoStream(stream));
  }

  public setLocalScreenStream(stream: MediaStream) {
    this.localScreenStream = stream;
    this.peers.forEach(peer => peer.setLocalScreenStream(stream));
  }

  public setMediaStatus({ audio, video }: IMediaStatus) {
    if (this.mediaStatus.audio !== audio || this.mediaStatus.video !== video) {
      this.mediaStatus = {
        ...this.mediaStatus,
        audio,
        video,
      };

      this.peers.forEach(peer => peer.sendMediaStatus(this.mediaStatus));
    }
  }

  public setLocalStreamSpeakingState(isSpeaking: boolean) {
    if (this.mediaStatus.speaking !== isSpeaking) {
      this.mediaStatus = {
        ...this.mediaStatus,
        speaking: isSpeaking,
      };

      this.peers.forEach(peer => peer.sendMediaStatus(this.mediaStatus));
    }
  }

  public async send<
    Action extends StreamActions.INSERT | StreamActions.APPEND,
    StreamName extends Stream,
    Message extends IStreamActionWithPayloadMap<StreamName, EncodedAction<StreamName>>[Action],
    R extends Message['action'] & IEncodedPersistedAction,
  >(
    type: Action,
    message: Message,
  ): Promise<R>;
  public async send<
    StreamName extends Stream,
    Message extends ILockAction<StreamName>,
    R extends StreamPosition
  >(
    type: StreamActions.LOCK,
    message: Message,
  ): Promise<R>;
  public async send<
    Streams extends Stream,
    Message extends ISubscriptionRequest<Streams>,
    RS extends RequestedStreams<Message['streams']>,
    R extends ISubscriptionResponse<RS>
  >(
    action: StreamActions.SUBSCRIBE,
    message: Message,
  ): Promise<R>;
  public async send<
    Action extends keyof IRoomActionsPayload,
    R extends IRoomActionsReturnTypes[Action]
  >(
    type: Action,
    message: IRoomActionsPayload[Action],
  ): Promise<R>;
  public async send<
    Action extends StreamActions | RoomActions,
    StreamName extends Stream,
    Message = unknown,
    R = ValuesOf<IStreamActionWithPayloadReturnTypes<StreamName>> | ValuesOf<IRoomActionsReturnTypes>,
  >(
    type: Action,
    message: Message,
  ): Promise<R> {
    if (!this.socket) {
      throw new Error('Trying to send a Room message without Socket instance');
    }

    if (!this.authorized && type !== 'authorize') {
      throw new SocketAbortedError({
        response: 'Must authorize to Realtime server first',
        action: type,
        payload: message,
      });
    }

    if (this.authorized && !this.joined && type !== 'join') {
      // Only valid message before we join room is 'join', reject any other
      throw new SocketAbortedError({
        response: 'No room joined yet',
        action: type,
        payload: message,
      });
    }

    try {
      const timeout = sleep(ACK_TIMEOUT).then(() => Promise.reject(
        new SocketTimeoutError({
          response: `Waited ${ACK_TIMEOUT / 1000} seconds for ACK`,
          action: type,
          payload: message,
        })),
      );

      if (type !== RoomActions.Stats) {
        Logger.debug(`out '${type}'`, message);
      }

      const result = await Promise.race([
        callbackAsPromise<
          Promise<R>,
          Error,
          Action,
          Message
        >(this.socket.emit.bind(this.socket), type, message),
        timeout,
      ]);

      if (type !== RoomActions.Stats) {
        Logger.debug(`in ack '${type}'`, result);
      }

      return result;
    } catch (error) {
      FluentdLogger.warn({
        name:         'Room.send',
        message_type: type,
        message_args: message,
        room_id:      this.id,
        socket_url:   this.url,
        error:        dumpErrorInfo(error),
      });
      throw error;
    }
  }

  private async dispatchReceivedMessage(message: IRelayedMessage) {
    const fromPeerId = decodeOrElse(
      IdentityEntityStringIdCodec,
      message.from,
      () => null,
    );

    if (!fromPeerId) return null;

    const peer = this.peers.get(fromPeerId);

    if (!peer) return null;

    return peer.dispatch(message);
  }

  public async stats(payload: DistributiveOmit<RtcStatsEvent, 'timestamp'>) {
    if (!this.joined) return;

    const serverTime = new Date(Date.now() + this.serverTimeDelta).toISOString();

    try {
      await this.send(RoomActions.Stats, {
        timestamp: serverTime,
        ...payload,
      });
    } catch (error) {
      if (!(error instanceof TimeoutError) && !(error instanceof AbortedError)) {
        notify(error, { name: 'RTCRoom.stats' });
      }
    }
  }

  public getActionStream<S extends Stream>(name: S) {
    let channel = this.actionStreams.get(name);

    if (!channel) {
      channel = new ActionStream(this, name);
      this.actionStreams.set(name, channel);
    }

    return channel;
  }

  public async sendMessage<T extends keyof IMessagePayload>(
    type: T,
    to: EntityStringId,
    rest: IMessagePayload[T],
  ) {
    return await this.send(RoomActions.Message, { type, to, ...rest });
  }

  /**
   * Sends a command to a peer.
   */
  public async sendBotCommand(
    peerId: EntityStringId,
    botCommandArgs: BotCommandArgs,
  ): Promise<IBotCommandAck> {
    if (!this.peers.get(peerId)?.isBot) {
      throw new Error(`${peerId} is not a bot.`);
    }

    try {
      const result = await this.sendMessage(
        BotMessageType.Command,
        peerId,
        botCommandArgs,
      );

      const { botState } = result;
      this.emit(RTC_ROOM_EVENTS.botStateChange, peerId, botState);

      return result;
    } catch (err) {
      // If an error occurs, it will still provide the bot state.
      if (err && err.botState) {
        this.emit(RTC_ROOM_EVENTS.botStateChange, peerId, err.botState);
      }

      throw err;
    }
  }

  /**
   * Adds a peer.
   * @param synthetic true if the event corresponds to a peer joining, false
   *                  when we're is generating peerJoined events for existing peers.
   */
  private peerJoined({
      peer: {
        clientData,
        id,
        isBot,
      },
    }: IPeerJoinedEvent,
    synthetic = false,
  ) {
    if (!this.clientFeatures) {
      notify(
        new Error('Client features are not set, this should not happen'),
      );

      return;
    }

    const decodedClientData = decodeOrElse(
      ClientDataDecoder,
      clientData,
      () => ({ features: {}, upgradedToUser: false }),
    );

    const { features: clientFeatures, upgradedToUser } = decodedClientData;

    if (this.peers.has(id)) {
      notify(new Error('Peer re-joined without leaving. This should never happen.'), {
        name: 'RTCRoom.PeerRejoin',
        context: { peerId: id },
      });

      return;
    }

    if (!this.caller) {
      notify(new Error('Peer join event happened yet caller is not set'));
      return;
    }

    const peer = new RTCPeer({
      id,
      isBot,
      room:        this,
      audioStream: this.localAudioStream,
      videoStream: this.localVideoStream,
      screenStream: this.localScreenStream,
      // Offerer is determined by caller id. We're the offerer if
      // our id is smaller than the other peer
      isOfferer:   this.caller.id < id,
      features:    featuresIntersection(
        this.clientFeatures,
        clientFeatures,
      ),
    });

    this.peers.set(id, peer);

    peer.sendMediaStatus(this.mediaStatus);

    // If the peer is a bot, ping it to get the initial state.
    if (isBot) {
      this.sendBotCommand(id, { command: BotCommand.GetBotState, version: BotVersion });
    }

    // Creating a serialization of the participant is currently necessary since the representation
    // of the peer in the messages from the real time server is not uniform.
    this.emit(RTC_ROOM_EVENTS.addPeer, id, synthetic);

    // On inital room join synthetic events are fired for each peer
    // Previously upgraded peers will still have the flag in their clientData
    // We only want to signal about upgrade if a peer has joined
    // Not upon us joining the room
    if (!synthetic && upgradedToUser) {
      this.emit(RTC_ROOM_EVENTS.peerUpgradedToUser);
    }
  }

  private peerLeft({ id }: IPeerLeaveEvent) {
    const peer = this.peers.get(id);

    if (!peer) return;

    peer.close();

    this.emit(RTC_ROOM_EVENTS.removePeer, id, false);
    this.peers.delete(id);
  }

  public async subscribeToStream<StreamNames extends Stream>(name: StreamNames, lastPosition: StreamPosition) {
    try {
      const response = await this.send(StreamActions.SUBSCRIBE, {
        streams: [{ name, lastPosition }],
      });

      // For some reason if you destructure the immediate awaited
      // result, 'streams' are inferred as any
      const { streams } = response;

      return streams[name];
    } catch (error) {
      notify(error, {
        name: 'streamSubscribe',
        context: { stream: name, lastPosition },
      });
      throw error;
    }
  }

  public updateParticipantName(name: string) {
    const trimmedName = this.trimParticipantName(name);
    this.send(RoomActions.ChangeName, { name: trimmedName });
  }

  private trimParticipantName(name: string) {
    return name.substring(0, PARTICIPANT_NAME_MAX_LENGTH);
  }
}

/**
 * Creates a function that will handle callbacks to a socket event
 * and print in and out data to the console.
 *
 * Note that the returned function uses `function()` syntax to be
 * able to bind `this`
 */
const wrapReceive = <
  F extends (...args: any[]) => any,
>(
  type: RoomEvents | StreamEvents, fn: F,
) => async function (this: typeof fn, ...args: Parameters<F>) {
  let callback;
  const lastArg = args[args.length - 1];

  if (typeof lastArg === 'function') {
    callback = (...ackArgs: unknown[]) => {
      Logger.debug(`out ack '${type}'`, ackArgs);
      lastArg(...ackArgs);
    };
    args.pop();
  }

  if (type !== RoomEvents.Stats) {
    Logger.debug(`in '${type}'`, ...args);
  }

  try {
    const response = await fn.call(this, ...args);

    if (callback) {
      callback(null, response);
    }
  } catch (error) {
    if (callback) {
      callback({ code: error.name, detail: error.message });
    } else {
      throw error;
    }
  }
};

